/**
 * Central manifest for every video on the public marketing site.
 *
 * Single source of truth (like `marketing-pricing.ts` is for prices): pages and
 * components never hardcode a video path — they resolve an entry here. The
 * entries describe only *final, mastered* deliverables; the sync tool
 * (`tools/website/sync-video-assets.ts`, `npm run video:web:sync`) copies those
 * into `public/media/videos/` (faststart-remuxed) and generates the posters.
 *
 * Availability contract (enforced by `marketing-videos.test.ts`):
 *   - "published"  → mp4 + captions + poster + transcript all exist in
 *                    `public/`; the page renders the real player and may emit
 *                    VideoObject structured data.
 *   - "pending"    → the final deliverable does not exist yet. Pages render an
 *                    honest "coming soon" note — never an old/raw cut, never a
 *                    fake placeholder. Nothing is emitted in structured data.
 *   - "preview"    → playable but explicitly labelled as a preview; no general
 *                    availability claim, no structured data.
 *   - "hidden"     → not rendered, not linked, not in the sitemap.
 *
 * The transcript is content of the (Dutch) video, not UI copy, so it lives
 * here verbatim rather than in the i18n dictionaries; components render it
 * inside `lang="nl"` and label the language explicitly on non-Dutch pages.
 * UI strings around the player DO go through i18n (`videoKit.*`).
 */

export type MarketingVideoId =
  | "fixcontrol-overview-nl"
  | "fixcontrol-support-nl"
  | "fixcontrol-support-short-nl"
  | "fixcontrol-engineering-nl"
  | "fixcontrol-engineering-short-nl"
  | "fixcontrol-hr-nl"
  | "fixcontrol-hr-short-nl";

export type MarketingVideoKind = "overview" | "use-case" | "use-case-short";

export type MarketingVideoAvailability = "published" | "preview" | "pending" | "hidden";

/** Analytics group — becomes part of the fixed event names in `track.ts`. */
export type MarketingVideoAnalyticsName = "overview" | "support" | "engineering" | "hr";

export type TranscriptSection = {
  /** Short plain-language heading for this part of the video. */
  title: string;
  /** The spoken voice-over of this part, verbatim. */
  paragraphs: string[];
};

export type MarketingVideo = {
  id: MarketingVideoId;
  /** Spoken/caption language of the deliverable. */
  locale: "nl";
  kind: MarketingVideoKind;
  /**
   * Title/description of the video itself (in the video's language) — used
   * for VideoObject structured data and as accessible fallback. On-page copy
   * around the player comes from the page's own i18n namespace.
   */
  title: string;
  description: string;
  durationSeconds: number;
  width: number;
  height: number;
  aspectRatio: "16:9";
  /** Site-relative public asset paths (under /media/videos/…). */
  mp4: string;
  captionsVtt: string | null;
  poster: { avif: string; webp: string; jpg: string } | null;
  transcript: TranscriptSection[] | null;
  /** Marketing route where this video primarily lives. */
  route: string;
  analyticsName: MarketingVideoAnalyticsName;
  availability: MarketingVideoAvailability;
  /** Bumped when the deliverable is replaced; part of asset versioning. */
  contentVersion: number;
  /**
   * Publication date of the deliverable on the site. Full ISO 8601 datetime
   * WITH timezone offset (Search Console rejects date-only values). Null until
   * published.
   */
  uploadDate: string | null;
};

const OVERVIEW_TRANSCRIPT: TranscriptSection[] = [
  {
    title: "AI kent uw organisatie niet vanzelf",
    paragraphs: [
      "AI kan veel, maar weet niet automatisch wat er binnen uw organisatie speelt.",
      "Informatie staat verspreid over tickets, code, gesprekken en documenten.",
    ],
  },
  {
    title: "De juiste informatie per project",
    paragraphs: ["FixControl koppelt per project alleen de bronnen die daarbij horen."],
  },
  {
    title: "Gespecialiseerde AI-agents bereiden werk voor",
    paragraphs: [
      "Gespecialiseerde AI-agents onderzoeken het probleem, analyseren relevante code en documentatie, en bereiden een antwoord, analyse of wijzigingsvoorstel voor.",
    ],
  },
  {
    title: "Controle vóór actie",
    paragraphs: [
      "Daarna controleert FixControl welke regels gelden, of tests zijn geslaagd, welk risico is vastgesteld, en wie toestemming moet geven.",
    ],
  },
  {
    title: "Een medewerker beslist",
    paragraphs: [
      "Een medewerker ziet het voorstel en de controles, en kan goedkeuren, afwijzen of een aanpassing vragen.",
    ],
  },
  {
    title: "Uw systemen blijven de bron",
    paragraphs: [
      "Tickets, code en gesprekken blijven in de bestaande systemen.",
      "Waar ingesteld worden goedkeuringen gedaan via Slack of Teams.",
    ],
  },
  {
    title: "Pas na goedkeuring een pull request",
    paragraphs: [
      "Pas na de vereiste goedkeuring wordt de wijziging als pull request voorbereid.",
    ],
  },
  {
    title: "Zo helpt FixControl u verder",
    paragraphs: [
      "Eerst de juiste informatie. Daarna een voorstel. Pas na controle een actie. Zo helpt FixControl u verder.",
    ],
  },
];

/**
 * The manifest. Order is presentation order (overview first). Paths follow
 * `/media/videos/<group>/<id>[.-…]` and are produced exclusively by the sync
 * tool — never hand-copied files.
 */
export const MARKETING_VIDEOS: readonly MarketingVideo[] = [
  {
    id: "fixcontrol-overview-nl",
    locale: "nl",
    kind: "overview",
    title: "FixControl in één minuut",
    description:
      "Hoe FixControl per project de juiste informatie samenbrengt, AI-agents een voorstel laat voorbereiden en controleert wat er daarna mag gebeuren — met een menselijke beslissing waar dat nodig is.",
    durationSeconds: 66.21,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/overview/fixcontrol-overview-nl.mp4",
    captionsVtt: "/media/videos/overview/fixcontrol-overview-nl.vtt",
    poster: {
      avif: "/media/videos/overview/fixcontrol-overview-nl-poster.avif",
      webp: "/media/videos/overview/fixcontrol-overview-nl-poster.webp",
      jpg: "/media/videos/overview/fixcontrol-overview-nl-poster.jpg",
    },
    transcript: OVERVIEW_TRANSCRIPT,
    route: "/",
    analyticsName: "overview",
    availability: "published",
    contentVersion: 8,
    uploadDate: "2026-07-19T00:00:00+02:00",
  },
  {
    id: "fixcontrol-support-nl",
    locale: "nl",
    kind: "use-case",
    title: "Van klantvraag naar gecontroleerd antwoord",
    description:
      "Hoe FixControl een klantvraag aan het juiste project koppelt, een antwoord voorbereidt en controles toepast voordat het wordt verstuurd.",
    durationSeconds: 48.34,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/support/fixcontrol-support-nl.mp4",
    captionsVtt: "/media/videos/support/fixcontrol-support-nl.vtt",
    poster: {
      avif: "/media/videos/support/fixcontrol-support-nl-poster.avif",
      webp: "/media/videos/support/fixcontrol-support-nl-poster.webp",
      jpg: "/media/videos/support/fixcontrol-support-nl-poster.jpg",
    },
    transcript: [
      {
        title: "Een klantvraag komt binnen",
        paragraphs: ["Een klant meldt per e-mail dat de factuurexport steeds vastloopt."],
      },
      {
        title: "Het juiste project",
        paragraphs: [
          "FixControl koppelt de vraag aan het juiste project en gebruikt alleen de bijbehorende tickets, gesprekken en documentatie.",
        ],
      },
      {
        title: "Een antwoord wordt voorbereid",
        paragraphs: [
          "Een gespecialiseerde agent onderzoekt het probleem en bereidt een duidelijk antwoord voor.",
        ],
      },
      {
        title: "Controle vóór versturen",
        paragraphs: [
          "Daarna controleert FixControl of het antwoord veilig is, welke regels gelden en of een medewerker moet goedkeuren.",
        ],
      },
      {
        title: "Een medewerker beslist",
        paragraphs: [
          "De medewerker ziet het voorgestelde antwoord en de gebruikte informatie en kan goedkeuren, aanpassen of afwijzen.",
        ],
      },
      {
        title: "Terug via het bestaande kanaal",
        paragraphs: [
          "Pas daarna wordt het antwoord via het bestaande helpdesksysteem of e-mailkanaal verstuurd.",
        ],
      },
      {
        title: "Sneller, met controle",
        paragraphs: [
          "Zo reageert uw team sneller, terwijl het controle houdt over wat er naar de klant gaat.",
        ],
      },
    ],
    route: "/use-cases/support",
    analyticsName: "support",
    availability: "published",
    contentVersion: 8,
    uploadDate: "2026-07-19T00:00:00+02:00",
  },
  {
    id: "fixcontrol-support-short-nl",
    locale: "nl",
    kind: "use-case-short",
    title: "Supportdemo — korte preview",
    description: "Korte, stille preview van de supportdemo voor de use-casekaart.",
    durationSeconds: 15.52,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/support/fixcontrol-support-nl-short.mp4",
    captionsVtt: null,
    poster: {
      avif: "/media/videos/support/fixcontrol-support-nl-short-poster.avif",
      webp: "/media/videos/support/fixcontrol-support-nl-short-poster.webp",
      jpg: "/media/videos/support/fixcontrol-support-nl-short-poster.jpg",
    },
    transcript: null,
    route: "/use-cases/support",
    analyticsName: "support",
    availability: "published",
    contentVersion: 8,
    uploadDate: "2026-07-19T00:00:00+02:00",
  },
  {
    id: "fixcontrol-engineering-nl",
    locale: "nl",
    kind: "use-case",
    title: "Van bugmelding naar getest wijzigingsvoorstel",
    description:
      "Hoe FixControl een bug onderzoekt, een codewijziging voorbereidt, tests controleert en na goedkeuring een pull request voorbereidt.",
    durationSeconds: 51.46,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/engineering/fixcontrol-engineering-nl.mp4",
    captionsVtt: "/media/videos/engineering/fixcontrol-engineering-nl.vtt",
    poster: {
      avif: "/media/videos/engineering/fixcontrol-engineering-nl-poster.avif",
      webp: "/media/videos/engineering/fixcontrol-engineering-nl-poster.webp",
      jpg: "/media/videos/engineering/fixcontrol-engineering-nl-poster.jpg",
    },
    transcript: [
      {
        title: "Een bug wordt gemeld",
        paragraphs: ["Een klant meldt dat de factuurexport vastloopt bij grote bestanden."],
      },
      {
        title: "Project en repository",
        paragraphs: [
          "FixControl koppelt het ticket aan het project Facturatie en gebruikt de bijbehorende repository, documentatie en eerdere meldingen.",
        ],
      },
      {
        title: "Agents onderzoeken de oorzaak",
        paragraphs: [
          "Gespecialiseerde agents onderzoeken het probleem en analyseren de relevante code.",
        ],
      },
      {
        title: "Wijziging voorbereid en getest",
        paragraphs: [
          "Daarna bereidt FixControl een mogelijke wijziging voor. Die wordt getest in een afgeschermde omgeving.",
        ],
      },
      {
        title: "Testresultaten, risico en goedkeuring",
        paragraphs: [
          "FixControl controleert de testresultaten, het risico en wie toestemming moet geven.",
        ],
      },
      {
        title: "Een engineer beslist",
        paragraphs: [
          "Een engineer kan het voorstel goedkeuren, afwijzen of om een aanpassing vragen.",
        ],
      },
      {
        title: "Pull request na goedkeuring",
        paragraphs: [
          "Pas na de vereiste goedkeuring wordt de wijziging als pull request voorbereid. Niet rechtstreeks op de hoofdbranch.",
        ],
      },
    ],
    route: "/use-cases/engineering",
    analyticsName: "engineering",
    availability: "published",
    contentVersion: 8,
    uploadDate: "2026-07-19T00:00:00+02:00",
  },
  {
    id: "fixcontrol-engineering-short-nl",
    locale: "nl",
    kind: "use-case-short",
    title: "Engineeringdemo — korte preview",
    description: "Korte, stille preview van de engineeringdemo voor de use-casekaart.",
    durationSeconds: 14.69,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/engineering/fixcontrol-engineering-nl-short.mp4",
    captionsVtt: null,
    poster: {
      avif: "/media/videos/engineering/fixcontrol-engineering-nl-short-poster.avif",
      webp: "/media/videos/engineering/fixcontrol-engineering-nl-short-poster.webp",
      jpg: "/media/videos/engineering/fixcontrol-engineering-nl-short-poster.jpg",
    },
    transcript: null,
    route: "/use-cases/engineering",
    analyticsName: "engineering",
    availability: "published",
    contentVersion: 7,
    uploadDate: "2026-07-19T00:00:00+02:00",
  },
  {
    id: "fixcontrol-hr-nl",
    locale: "nl",
    kind: "use-case",
    title: "Van HR-vraag naar een antwoord waar iemand voor tekent",
    description:
      "Hoe FixControl eerst bepaalt wie een HR-vraag mag zien, het antwoord alleen-lezen en geminimaliseerd uit het HR-systeem haalt, en een gevoelig antwoord pas levert na goedkeuring door een tweede persoon.",
    durationSeconds: 51.9,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/hr/fixcontrol-hr-nl.mp4",
    captionsVtt: "/media/videos/hr/fixcontrol-hr-nl.vtt",
    poster: {
      avif: "/media/videos/hr/fixcontrol-hr-nl-poster.avif",
      webp: "/media/videos/hr/fixcontrol-hr-nl-poster.webp",
      jpg: "/media/videos/hr/fixcontrol-hr-nl-poster.jpg",
    },
    transcript: [
      {
        title: "Een HR-vraag komt binnen",
        paragraphs: [
          "Een medewerker vraagt in Microsoft Teams hoeveel ouderschapsverlof zij nog heeft.",
        ],
      },
      {
        title: "Eerst: wie mag dit zien?",
        paragraphs: [
          "FixControl bepaalt eerst wie dit mag zien: de medewerker en de behandelaar, niet het hele team.",
        ],
      },
      {
        title: "Alleen-lezen, en eerlijk als er geen cijfer is",
        paragraphs: [
          "FixControl leest het saldo alleen-lezen uit het HR-systeem: alleen het veld dat nodig is. Is er geen cijfer, dan verzint FixControl niets — een mens neemt het over.",
        ],
      },
      {
        title: "Een gevoelige vraag gaat een andere route",
        paragraphs: [
          "Een gevoelige vraag gaat naar een bevoegde beoordelaar. Wie toewijst, ziet alleen kenmerken — niet de inhoud.",
        ],
      },
      {
        title: "Vier-ogen vóór levering",
        paragraphs: [
          "Een gevoelig antwoord gaat pas de deur uit na goedkeuring door een tweede persoon. Die goedkeuring is 72 uur geldig.",
        ],
      },
      {
        title: "Vastgelegd en herstelbaar",
        paragraphs: [
          "Elke stap is vastgelegd. Klopt iets niet, dan volgt een gepubliceerde correctie.",
        ],
      },
      {
        title: "Sneller, met controle",
        paragraphs: ["Zo krijgen medewerkers sneller antwoord, terwijl HR de controle houdt."],
      },
    ],
    route: "/use-cases/hr-governed-answers",
    analyticsName: "hr",
    availability: "published",
    // 4: verse HR-captures — Nederlandse notatie in het antwoord (73,5 uur,
    // peildatum 27 juli 2026), vertaalde statuslabels en de behandelaar met
    // naam in plaats van een user-id.
    contentVersion: 4,
    uploadDate: "2026-07-26T00:00:00+02:00",
  },
  {
    id: "fixcontrol-hr-short-nl",
    locale: "nl",
    kind: "use-case-short",
    title: "HR-demo — korte preview",
    description: "Korte, stille preview van de HR-demo voor de use-casekaart.",
    durationSeconds: 15.8,
    width: 1920,
    height: 1080,
    aspectRatio: "16:9",
    mp4: "/media/videos/hr/fixcontrol-hr-nl-short.mp4",
    captionsVtt: null,
    poster: {
      avif: "/media/videos/hr/fixcontrol-hr-nl-short-poster.avif",
      webp: "/media/videos/hr/fixcontrol-hr-nl-short-poster.webp",
      jpg: "/media/videos/hr/fixcontrol-hr-nl-short-poster.jpg",
    },
    transcript: null,
    route: "/use-cases/hr-governed-answers",
    analyticsName: "hr",
    availability: "published",
    contentVersion: 2,
    uploadDate: "2026-07-26T00:00:00+02:00",
  },
];

/** Resolve a manifest entry. Throws on an unknown id so a typo fails loudly. */
export function getMarketingVideo(id: MarketingVideoId): MarketingVideo {
  const v = MARKETING_VIDEOS.find((x) => x.id === id);
  if (!v) throw new Error(`Unknown marketing video id: ${id}`);
  return v;
}

/** A video may render a real player only when published (or preview, labelled). */
export function isPlayable(v: MarketingVideo): boolean {
  return v.availability === "published" || v.availability === "preview";
}

/** ISO-8601 duration ("PT1M6S") for VideoObject structured data. */
export function isoDuration(seconds: number): string {
  const whole = Math.round(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `PT${m > 0 ? `${m}M` : ""}${s}S`;
}

/** Human "M:SS" duration label for poster badges. */
export function formatDuration(seconds: number): string {
  const whole = Math.round(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * VideoObject structured data for a PUBLISHED video. Returns null for any
 * other availability so callers can render unconditionally. Only real data:
 * no invented view counts, ratings or publish dates.
 */
export function buildVideoObjectJsonLd(
  v: MarketingVideo,
  absoluteUrl: (path: string) => string,
): Record<string, unknown> | null {
  if (v.availability !== "published" || !v.poster || !v.uploadDate) return null;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${absoluteUrl(v.route)}#video-${v.id}`,
    name: v.title,
    description: v.description,
    thumbnailUrl: [absoluteUrl(v.poster.jpg)],
    uploadDate: v.uploadDate,
    duration: isoDuration(v.durationSeconds),
    contentUrl: absoluteUrl(v.mp4),
    inLanguage: "nl-NL",
  };
}
